import React, { useState, useRef, useEffect } from "react";
import {
  FaBell,
  FaSearch,
  FaSignOutAlt,
  FaCog
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";

export default function Topbar() {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [confirmLogout, setConfirmLogout] = useState(false); // 🔥 NEW

  const dropdownRef = useRef(null);
  const profileRef = useRef(null);
  const socketRef = useRef(null);

  /* ===============================
     🔐 USER
  =============================== */
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "AU";

  /* ===============================
     🔔 SOCKET
  =============================== */
  useEffect(() => {
    socketRef.current = io("http://localhost:5000", {
      transports: ["websocket"]
    });

    socketRef.current.on("receiveNotification", (data) => {
      setNotifications((prev) => [
        { ...data, time: "Just now", read: false },
        ...prev
      ]);
    });

    return () => socketRef.current?.disconnect();
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  /* ===============================
     OUTSIDE CLICK
  =============================== */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ===============================
     MARK AS READ
  =============================== */
  const markAllAsRead = () => {
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read: true }))
    );
  };

  const markAsRead = (index) => {
    setNotifications((prev) =>
      prev.map((n, i) =>
        i === index ? { ...n, read: true } : n
      )
    );
  };

  useEffect(() => {
    if (open) markAllAsRead();
  }, [open]);

  /* ===============================
     🔐 LOGOUT FIXED
  =============================== */
  const handleLogout = () => {
    console.log("🚪 Logging out...");

    localStorage.removeItem("token");
    localStorage.removeItem("user");

    socketRef.current?.disconnect();

    // 🔥 FORCE APP AUTH REFRESH
    window.dispatchEvent(new Event("storage"));

    navigate("/login", { replace: true });
  };

  /* =============================== */
  const handleSettings = () => {
    navigate("/settings");
  };

  /* =============================== */
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      console.log("🔍 Search:", searchQuery);
    }
  };

  return (
    <>
      <div className="fixed top-0 left-0 md:left-64 right-0 h-16 
        bg-white/80 backdrop-blur-xl border-b z-30 flex items-center justify-end 
        px-4 md:px-6 space-x-4 shadow-sm">

        {/* SEARCH */}
        <div className="relative w-40 md:w-64">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearch}
            className="w-full pl-10 pr-3 py-2 rounded-xl border text-sm 
            focus:ring-2 focus:ring-green-500 outline-none transition hover:shadow-sm"
          />
        </div>

        {/* NOTIFICATIONS */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen(!open)}
            className="relative text-[#2D5D46] hover:scale-110 transition"
          >
            <FaBell className="text-xl md:text-2xl" />

            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 text-[10px] bg-red-500 text-white px-1.5 rounded-full animate-pulse">
                {unreadCount}
              </span>
            )}
          </button>

          <div className={`absolute right-0 mt-3 w-80 bg-white rounded-xl shadow-xl border transition-all ${
            open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
          }`}>
            <div className="flex justify-between px-4 py-3 border-b">
              <h3 className="font-semibold text-sm">Notifications</h3>

              <button
                onClick={markAllAsRead}
                className="text-xs text-green-600 hover:underline"
              >
                Mark all
              </button>
            </div>

            {notifications.length === 0 && (
              <div className="p-4 text-sm text-gray-500 text-center">
                No notifications yet
              </div>
            )}

            {notifications.map((n, i) => (
              <div
                key={i}
                onClick={() => markAsRead(i)}
                className={`px-4 py-3 border-t cursor-pointer transition ${
                  !n.read ? "bg-green-50 hover:bg-green-100" : "hover:bg-gray-50"
                }`}
              >
                <p className="text-sm font-semibold">{n.title}</p>
                <p className="text-xs text-gray-500">{n.desc}</p>
                <span className="text-[10px] text-gray-400">{n.time}</span>
              </div>
            ))}
          </div>
        </div>

        {/* PROFILE */}
        <div className="relative" ref={profileRef}>
          <div
            onClick={() => setProfileOpen(!profileOpen)}
            className="w-10 h-10 rounded-full bg-gradient-to-r from-green-600 to-green-500 text-white flex items-center justify-center font-bold cursor-pointer shadow hover:scale-105 transition"
          >
            {initials}
          </div>

          <div className={`absolute right-0 mt-3 w-44 bg-white rounded-xl shadow-lg border transition-all ${
            profileOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
          }`}>
            <button
              onClick={handleSettings}
              className="flex items-center gap-2 w-full px-4 py-3 hover:bg-gray-50 transition"
            >
              <FaCog /> Settings
            </button>

            <button
              onClick={() => setConfirmLogout(true)}
              className="flex items-center gap-2 w-full px-4 py-3 text-red-500 hover:bg-red-50 transition"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* 🔥 LOGOUT MODAL */}
      {confirmLogout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">

          <div className="bg-white rounded-2xl shadow-xl p-6 w-80 text-center animate-fadeIn">

            <h3 className="text-lg font-semibold mb-2">
              Confirm Logout
            </h3>

            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to logout?
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setConfirmLogout(false)}
                className="px-4 py-2 rounded-lg border hover:bg-gray-100 transition"
              >
                Cancel
              </button>

              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition"
              >
                Logout
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}